import React, { createContext, useContext, useEffect, useRef, useState } from "react";
import api, { ENGAGEMENT_STALE_EVENT } from "@/lib/api";
import { asArray } from "@/lib/asArray";
import { useAuth } from "@/contexts/AuthContext";
// Step 11d-3 · find 1: the selected engagement is per tab (sessionStorage); localStorage only starts a new tab
import { chooseEngagement, forgetActiveEngagement, keepTabEngagement, readActiveEngagement } from "@/lib/activeEngagement";

const EngCtx = createContext(null);
const CLEARED_KEY = "af_eng_cleared";

export function EngagementProvider({ children }) {
  const { user } = useAuth();
  const [engagements, setEngagements] = useState([]);
  const [currentEngId, setEngState] = useState(() => readActiveEngagement());
  // A choice (the top-bar picker, Engagements → Open, a dashboard link, the first engagement of a first visit): this tab keeps
  // it and it becomes the starting value of the next new tab. Another tab is never changed.
  const setCurrentEngId = (id) => {
    if (id) chooseEngagement(id);
    setEngState(id || "");
  };
  const [loading, setLoading] = useState(false);
  const staleRef = useRef(false);
  // Batch 12 · L5: set when a remembered engagement was cleared (another firm's, deleted) — kept across a page reload so
  // the "first visit picks the first engagement" rule does not switch the user by itself; removed when the user chooses
  const clearedRef = useRef((() => { try { return localStorage.getItem(CLEARED_KEY) === "1"; } catch { return false; } })());
  const markCleared = () => { clearedRef.current = true; try { localStorage.setItem(CLEARED_KEY, "1"); } catch { /* ignore */ } };

  const refresh = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const r = await api.get("/engagements");
      const list = asArray(r.data);   // Batch 12 · L5: a non-array reply never crashes the pages
      setEngagements(list);
      // Batch 3 · 3.5 (R51) — never switch the user to another engagement by itself. A first
      // visit with nothing remembered picks the first one; a remembered engagement that is no
      // longer in the list is cleared, and the pages ask the user to choose.
      // Batch 12 · L5: only the FIRST list read of a sign-in may pick the first engagement; once a remembered one was
      // cleared (another firm's, deleted), later reads never pick one by themselves — the pages ask the user to choose.
      const remembered = staleRef.current ? "" : currentEngId;
      if (!remembered && !staleRef.current && !clearedRef.current && list.length) {
        setCurrentEngId(list[0].id);
      } else if (remembered && !list.find((e) => e.id === remembered)) {
        markCleared();
        forgetActiveEngagement();
        setEngState("");
      }
    } catch { /* the list read failed — keep what is shown; the global toast says why */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (user) refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Batch 12 · L5 (F-92): the remembered engagement belongs to another firm or was deleted — clear the selection and
  // re-read this firm's list; the pages then ask the user to choose (no crash, no silent switch).
  useEffect(() => {
    const onStale = () => {
      staleRef.current = true; markCleared();
      forgetActiveEngagement();
      setEngState("");
      refresh().finally(() => { staleRef.current = false; });
    };
    window.addEventListener(ENGAGEMENT_STALE_EVENT || "af-engagement-stale", onStale);
    return () => window.removeEventListener(ENGAGEMENT_STALE_EVENT || "af-engagement-stale", onStale);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  useEffect(() => {
    if (currentEngId) {      // the user's own choice (or a valid remembered one) ends the "cleared" state
      keepTabEngagement(currentEngId);   // the tab keeps its engagement across a reload (the starting value is written on a choice)
      if (!staleRef.current) { clearedRef.current = false; try { localStorage.removeItem(CLEARED_KEY); } catch { /* ignore */ } }
    }
  }, [currentEngId]);

  const current = engagements.find((e) => e.id === currentEngId) || null;

  return (
    <EngCtx.Provider
      value={{ engagements, current, currentEngId, setCurrentEngId, refresh, loading }}
    >
      {children}
    </EngCtx.Provider>
  );
}

export const useEngagement = () => useContext(EngCtx);
